import dayjs from 'dayjs'
import { ref, shallowRef, triggerRef } from 'vue'

import { useNotification } from '@/entities/use-notification'
import {
  forgetAllPickupConnectedParties,
  forgetPickupConnectedParty,
  getConnectedPartyFromPickupEvent,
  rememberPickupConnectedParty,
  SIP_SUBSCRIBE_EXPIRES_DEFAULT_SECONDS,
} from '@/entities/webrtc'

import { NotifyEventData } from '@/shared/components/notifications'
import { isWindowHidden } from '@/shared/services/window-visibility'
import { isStageOrDevelopment } from '@/shared/utils/import-meta'

import { CallNotificationType, CallNotificationValue } from '..'

export const notifications = shallowRef<Map<CallNotificationValue['eventData']['callId'], CallNotificationValue>>(new Map())
export const notificationsHistory = ref<Array<Partial<CallNotificationValue> & { action: '+' | '-', callId: string }>>([])

let cleanupIntervalId: NodeJS.Timeout

const toMapKey = (type: CallNotificationType, callId: string) => `${type}:${callId}`

const keysFor = (callId: string, type?: CallNotificationType): string[] => {
  if (type) {
    return [callId, toMapKey(type, callId)]
  }
  return [callId, toMapKey('pickup', callId), toMapKey('self-pickup', callId)]
}

const removeById = (id: string): void => {
  const notification = notifications.value.get(id)
  const callId = notification?.eventData.callId ?? id
  if (notification?.type === 'pickup') {
    forgetPickupConnectedParty(callId)
  }
  useNotification().closePickupCallNotification(callId)
  if (isStageOrDevelopment()) {
    notificationsHistory.value.push({ callId, timestamp: dayjs().unix(), action: '-' })
  }
  if (notifications.value.has(id)) {
    notifications.value.delete(id)
    triggerRef(notifications)
  }
}

export const cleanupExpiredNotifications = (): void => {
  const currentTimestamp = dayjs().unix()
  const expiredIds = Array.from(notifications.value.entries())
    .filter(([, { timestamp }]) => (currentTimestamp - timestamp) > SIP_SUBSCRIBE_EXPIRES_DEFAULT_SECONDS)
    .map(([id]) => id)

  if (expiredIds.length > 0) {
    expiredIds.forEach(removeById)
  }
}

export const useCallsNotifications = () => {
  if (!cleanupIntervalId) {
    cleanupIntervalId = setInterval(
      cleanupExpiredNotifications,
      SIP_SUBSCRIBE_EXPIRES_DEFAULT_SECONDS * 1000,
    )
  }
  const add = (value: NotifyEventData, type: CallNotificationType = 'pickup'): void => {
    const notification = {
      type,
      eventData: value,
      timestamp: dayjs().unix(),
    }
    const existing = notifications.value.get(value.callId)
    const key = existing && existing.type !== type ? toMapKey(type, value.callId) : value.callId
    notifications.value.set(key, notification)
    const connectedParty = type === 'pickup' ? getConnectedPartyFromPickupEvent(value) : undefined
    if (connectedParty) {
      rememberPickupConnectedParty(value.callId, connectedParty)
    }
    const dialNumber = type === 'pickup' ? (value.interceptNumber || value.target) : value.from
    isWindowHidden().then((hidden) => {
      if (hidden) {
        useNotification().createPickupCallNotification(value.fromName, value.targetName, dialNumber, value.callId)
      }
    })
    if (isStageOrDevelopment()) {
      notificationsHistory.value.push({ ...notification, callId: value.callId, action: '+' })
    }
    triggerRef(notifications)
  }

  const remove = (id: string, type?: CallNotificationType) => {
    keysFor(id, type).forEach((key) => {
      const notification = notifications.value.get(key)
      if (!notification) {
        return
      }
      if (type && notification.type !== type) {
        return
      }
      removeById(key)
    })
  }

  const removeByType = (type: CallNotificationType) => {
    Array.from(notifications.value.entries()).forEach(([id, notification]) => {
      if (notification.type === type) {
        removeById(id)
      }
    })
  }

  const removeBySubscriberNumber = (number: string) => {
    Array.from(notifications.value.keys()).forEach((id) => {
      const notification = notifications.value.get(id)
      if (!notification || notification.type !== 'pickup') {
        return
      }
      const { from, target } = notification.eventData
      if (from === number || target === number) {
        removeById(id)
      }
    })
  }

  const clear = () => {
    notifications.value.clear()
    forgetAllPickupConnectedParties()
    triggerRef(notifications)

    notificationsHistory.value = []
  }

  return {
    add,
    remove,
    removeByType,
    removeBySubscriberNumber,
    clear,
  }
}
